import DOMPurify from 'dompurify'
import { Bell, Eraser, PencilLine, ThumbsDown, ThumbsUp, UserCheck, Users } from 'lucide-react'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { api } from '../../configs/api'
import { useAuth } from '../../context/AuthContext'
import Label from '../form/Label'
import RichText from '../form/RichText/Index'
import Select from '../form/Select'
import DateField from '../form/input/DateField'
import Button from '../ui/button/Button'
import Accordion from './Accordion'
import Avatar from './Avatar'
import Card from './Card'
import EditButton from './EditButton'
import { daysFromToday } from './Kanban/TaskPreview'

export type NotesProps = {
    data: {
        id: number
        title: string
        date: string
        description: string
        author: string
        image?: string
        dateNotify?: string
        users: {
            id: string
            name: string
            color?: string
        }[]
        status: 0 | 1
        isExpired: boolean
    }[]
    handleSave: (values: any) => void
    setData: (data: any) => void
    handleEdit: () => void
    handleDelete: () => void
    setCurrentNote: (note: any) => void
    form: any
    isPositive: boolean
    setIsPositive: (isPositive: boolean) => void
}

const Notes = ({
    data,
    handleSave,
    setData,
    handleEdit,
    handleDelete,
    setCurrentNote,
    form,
    isPositive,
    setIsPositive
}: NotesProps) => {
    const { user, users } = useAuth()
    const anotherUsers = users.filter(item => item.id !== user?.userID)

    const changeNoteStatus = async ({ note }: { note: NotesProps['data'][number] }) => {
        if (!note || !note.id || !user) {
            return toast.error('Erro ao atualizar status da nota!')
        }

        try {
            const formattedValues = {
                id: note.id,
                status: note.status === 0 ? 1 : 0
            }
            const response = await fetch(`${api}business/changeStatusNote`, {
                method: 'POST',
                body: JSON.stringify(formattedValues)
            })
            const result = await response.json()
            if (!result.success) {
                toast.error('Erro ao atualizar status da nota!')
                return
            }

            // Atualiza o status da nota no contexto corretamente
            setData((prevData: any) => ({
                ...prevData,
                notes: prevData.notes.map((item: NotesProps['data'][number]) =>
                    item.id === note.id ? { ...item, status: formattedValues.status } : item
                )
            }))

            form.reset()
            toast.success('Nota atualizada com sucesso!')
        } catch (error) {
            console.log(error)
        }
    }

    const saveNote = (status: number) => {
        //* Status: 0->Aberto, 1->Fechado
        form.setValue('status', status)
        handleSave(form.getValues())
    }

    return (
        <div className='space-y-2'>
            <Accordion
                title={
                    <div className='flex items-center gap-2'>
                        <PencilLine className='w-5 h-5' />
                        <p className='font-semibold text-[14px]'>Criar nota</p>
                    </div>
                }
                className='!bg-brand-100 !border-brand-200/80 !text-brand-600 dark:!bg-brand-500/[0.12] dark:text-brand-500 dark:!border-brand-500/[0.3]'
            >
                <div className='space-y-4'>
                    <div className='grid grid-cols-1 md:grid-cols-[1fr_2fr_1.6fr] gap-4'>
                        <div>
                            <Label required>Data</Label>
                            <Controller
                                name='date'
                                control={form.control}
                                defaultValue={new Date().toLocaleDateString('pt-BR')}
                                render={({ field }) => (
                                    <DateField
                                        name='date'
                                        value={field.value || ''}
                                        onChange={field.onChange}
                                        setValue={form.setValue}
                                    />
                                )}
                            />
                        </div>

                        <div>
                            <Label>Encaminhar para</Label>
                            <Controller
                                name='users'
                                control={form.control}
                                render={({ field, fieldState }) => (
                                    <Select
                                        options={anotherUsers}
                                        onChange={field.onChange} // Atualiza o react-hook-form
                                        value={field.value || []} // Mantém o valor sincronizado
                                        allowNew
                                        key={'contact'}
                                        multiple
                                        icon={<UserCheck className='w-5 h-5' />}
                                        error={fieldState.error ? true : false}
                                    />
                                )}
                            />
                        </div>

                        <div className='flex items-start justify-end gap-4 w-full'>
                            <div>
                                <Label>Situação</Label>
                                <div
                                    className={`flex items-center gap-1 text-center mt-4 ${
                                        isPositive ? 'text-green-400' : 'text-red-400'
                                    }`}
                                    onClick={() => setIsPositive(!isPositive)}
                                >
                                    {isPositive ? <ThumbsUp className='w-5 h-5' /> : <ThumbsDown className='w-5 h-5' />}
                                    <p>{isPositive ? 'Positivo' : 'Negativo'}</p>
                                </div>
                            </div>

                            <div className='w-full'>
                                <Label required={isPositive}>Lembrete</Label>
                                <Controller
                                    name='dateNotify'
                                    control={form.control}
                                    rules={{ required: isPositive }}
                                    render={({ field, fieldState: { error } }) => (
                                        <div>
                                            <DateField
                                                name='dateNotify'
                                                value={field.value || ''}
                                                onChange={field.onChange}
                                                setValue={form.setValue}
                                            />

                                            {/* Exibe a mensagem de erro, se houver */}
                                            {error && <span className='text-red-500 text-sm'>xxx {error.message}</span>}
                                        </div>
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label required>Nota</Label>
                        <Controller
                            name='description'
                            control={form.control}
                            render={({ field }) => <RichText value={field.value} onChange={field.onChange} />}
                        />
                    </div>
                    <div className='flex items-center justify-between gap-2'>
                        <div className='flex items-center gap-2'>
                            <Button variant='primary' type='button' onClick={() => saveNote(0)} size='sm'>
                                Salvar
                            </Button>
                            {/* <Button variant='primary' type='button' onClick={() => saveNote(1)} size='sm'>
                                Salvar e fechar
                            </Button> */}
                        </div>
                        <Button
                            variant='outline'
                            size='sm'
                            type='button'
                            onClick={() => form.reset()}
                            startIcon={<Eraser className='w-4 h-4' />}
                        >
                            Limpar
                        </Button>
                    </div>
                </div>
            </Accordion>

            {data &&
                data.map((note, index) => {
                    const formattedDateNotify = note.dateNotify ? note.dateNotify.split('/').reverse().join('-') : null
                    const daysLeft = formattedDateNotify ? daysFromToday(formattedDateNotify) : 0

                    return (
                        <Card key={index} hasError={note.isExpired ? true : false}>
                            <div className='space-y-2'>
                                <div className='flex items-center gap-2'>
                                    <div className='flex items-center justify-between w-full gap-2'>
                                        <div className='flex items-center gap-2'>
                                            <Avatar image={note?.image} name={note.author} size={10} />
                                            <div className='flex flex-col'>
                                                <div className='flex items-center gap-2'>
                                                    <span className='font-semibold'>{note.author}</span>
                                                    <span className='text-xs text-gray-400'>{note.date}</span>
                                                </div>
                                                <div className='flex items-center gap-2'>
                                                    {/* Lembrete */}
                                                    {note.dateNotify && (
                                                        /*note.status === 0 &&*/ <div className='flex items-center gap-1'>
                                                            <Bell
                                                                className={`w-4 h-4 ${
                                                                    note.status === 0 ? 'text-error-500' : ''
                                                                }`}
                                                            />
                                                            <span className='text-sm text-gray-400'>
                                                                Lembrete em {note.dateNotify}
                                                            </span>
                                                            <p
                                                                className={
                                                                    note.status === 0 && daysLeft < 0
                                                                        ? 'text-red-500'
                                                                        : ''
                                                                }
                                                            >
                                                                {daysLeft === 0
                                                                    ? '(Hoje)'
                                                                    : daysLeft === -1
                                                                    ? '(Ontem)'
                                                                    : daysLeft === 1
                                                                    ? '(Amanhã)'
                                                                    : `(${daysLeft} ${
                                                                          Math.abs(daysLeft) === 1 ? 'dia' : 'dias'
                                                                      })`}
                                                            </p>
                                                        </div>
                                                    )}

                                                    {/* Profissionais */}
                                                    {note.users && note.users.length > 0 && (
                                                        <div className='flex items-center gap-1'>
                                                            <Users className='w-4 h-4 text-brand-400' />
                                                            {note.users.map((user: any, index: number) => (
                                                                <p className='text-sm text-gray-400'>
                                                                    {user.name}
                                                                    {index + 1 === note.users.length ? '' : ', '}
                                                                </p>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        {/* <NoteStatus note={note} changeNoteStatus={changeNoteStatus} /> */}
                                    </div>
                                </div>
                                <p
                                    className='text-sm'
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(note.description) }}
                                />
                                {Number(note.status) === 0 && (
                                    <EditButton
                                        handleClick={() => {
                                            setCurrentNote(note)
                                            handleEdit()
                                        }}
                                    />
                                )}
                            </div>
                        </Card>
                    )
                })}
        </div>
    )
}

export default Notes
